import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Upload, PenTool, CheckCircle, Clock, Shield, PartyPopper, ArrowRight } from 'lucide-react';

// Client actions
const CLIENT_TASKS = [
  { key: 'doc', label: 'Enviar documento', desc: 'Envie uma foto do seu documento com CPF' },
  { key: 'term', label: 'Assinar Termo de Filiacao', desc: 'Leia e assine o termo de adesao' },
];

// RecupNet steps (client only watches, no actions)
const RECUPNET_STEPS = [
  { key: 'PROTOCOL_SENT', label: 'Documentacao enviada para processamento', simple: 'Seus dados estao sendo processados pela nossa equipe.' },
  { key: 'ACTION_FILED', label: 'Processo protocolado', simple: 'Sua acao foi registrada junto a associacao de defesa do consumidor.' },
  { key: 'NEG_CLEARING', label: 'Baixa das negativacoes', simple: 'Os orgaos de protecao ao credito estao processando a remocao.' },
  { key: 'ALL_DONE', label: 'Processo concluido', simple: 'Todas as negativacoes foram removidas com sucesso!' },
];

const NEG_BUREAUS = [
  { key: 'NEG_SPC_CLEARED', label: 'SPC Brasil' },
  { key: 'NEG_BOA_VISTA_CLEARED', label: 'Boa Vista' },
  { key: 'NEG_CENPROT_CLEARED', label: 'CENPROT' },
  { key: 'NEG_SERASA_CLEARED', label: 'Serasa' },
];

export default function ClientDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await clientAPI.dashboard();
        setData(res.data);
      } catch { toast.error('Erro ao carregar dados'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="rn-spinner" style={{width:32,height:32}} /></div>;

  const cases = data?.cases || [];
  if (!cases.length) return (
    <div className="text-center py-20"><div className="glass-card max-w-md mx-auto">
      <div className="rn-spinner mx-auto mb-4" style={{width:32,height:32}} />
      <h3 className="font-display text-lg font-semibold mb-2">Configurando seu processo...</h3>
    </div></div>
  );

  const c = cases[0];
  const mt = new Set(c.milestones?.map(m => m.type) || []);
  const hasDoc = c.documents?.length > 0;
  const hasTerm = !!c.term;
  const clientDone = hasDoc && hasTerm;

  // RecupNet progress
  const allNegCleared = NEG_BUREAUS.every(b => mt.has(b.key));
  const someNegCleared = NEG_BUREAUS.some(b => mt.has(b.key));
  const negCount = NEG_BUREAUS.filter(b => mt.has(b.key)).length;

  const getRecupStep = (key) => {
    if (key === 'NEG_CLEARING') return allNegCleared ? 'done' : someNegCleared ? 'active' : mt.has('ACTION_FILED') ? 'active' : 'waiting';
    if (key === 'ALL_DONE') return allNegCleared ? 'done' : 'waiting';
    return mt.has(key) ? 'done' : 'waiting';
  };

  // Find active recupnet step
  const recupActive = RECUPNET_STEPS.findIndex(s => getRecupStep(s.key) === 'active' || getRecupStep(s.key) === 'waiting');

  // Progress — use backend canonical value
  const pct = c.progress || 0;

  return (
    <div className="space-y-6" data-testid="client-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Painel do Cliente</h1>
          <p className="text-sm text-white/50">Acompanhe o andamento da regularizacao do seu CPF.</p>
        </div>
        <span className="badge-neutral text-xs">{c.docType}: {c.docFormatted}</span>
      </div>

      {/* Banner */}
      {!clientDone && (
        <div className="rounded-2xl p-4 border" style={{background:'rgba(249,168,37,0.06)', borderColor:'rgba(249,168,37,0.2)'}}>
          <p className="text-sm text-white/80">Falta apenas concluir os passos abaixo para iniciarmos seu processo.</p>
        </div>
      )}
      {clientDone && !allNegCleared && (
        <div className="rounded-2xl p-4 border" style={{background:'rgba(99,203,182,0.06)', borderColor:'rgba(99,203,182,0.2)'}}>
          <p className="text-sm" style={{color:'var(--rn-green)'}}>Sua parte esta concluida. Nosso time ja esta atuando no seu processo.</p>
        </div>
      )}
      {allNegCleared && (
        <div className="rounded-2xl p-5 border text-center" style={{background:'rgba(99,203,182,0.1)', borderColor:'rgba(99,203,182,0.3)'}}>
          <CheckCircle size={40} className="mx-auto mb-2" style={{color:'var(--rn-green)'}} />
          <p className="font-display text-lg font-semibold" style={{color:'var(--rn-green)'}}>Seu CPF esta regularizado!</p>
          <p className="text-sm text-white/50 mt-1">Todas as negativacoes foram removidas com sucesso.</p>
        </div>
      )}

      {/* Progress Bar + Summary */}
      <div className="glass-card">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-white/70">
            {allNegCleared ? 'Processo concluido com sucesso!' :
             clientDone ? 'Sua documentacao foi validada e ja esta em processamento.' :
             !hasDoc && !hasTerm ? 'Envie seu documento e assine o termo para iniciarmos.' :
             !hasDoc ? 'Envie seu documento para continuar.' :
             'Assine o termo de filiacao para continuar.'}
          </p>
          <span className="font-display text-xl font-bold tabular-nums ml-4 flex-shrink-0" style={{color:'var(--rn-green)'}}>{pct}%</span>
        </div>
        {/* Horizontal progress bar */}
        <div
          className="w-full h-[11px] rounded-full overflow-hidden"
          style={{background:'rgba(255,255,255,0.06)'}}
          role="progressbar"
          aria-label="Progresso do processo"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full rounded-full"
            style={{
              width: `${pct}%`,
              background: 'linear-gradient(90deg, #63CBB6 0%, #6FD3C0 60%, #7CDBC8 100%)',
              boxShadow: pct > 0 ? '0 0 12px rgba(99,203,182,0.25)' : 'none',
              transition: 'width 0.8s ease-out',
            }}
          />
        </div>
        <div className="flex items-center gap-2 mt-2.5">
          <Shield size={13} className="text-white/20"/>
          <p className="text-[10px] text-white/25">Processo com base juridica e associacao de defesa do consumidor.</p>
        </div>
      </div>

      {/* ========== BLOCO 1: SUA ETAPA (Cliente) ========== */}
      <div className="glass-card">
        <h2 className="font-display text-lg font-semibold mb-1">Sua Etapa</h2>
        <p className="text-xs text-white/40 mb-4">Complete os passos abaixo para iniciarmos o processo.</p>

        {/* Client task cards */}
        <div className="space-y-3">
          {/* Document */}
          <button onClick={() => navigate(`/client/documents/${c.id}`)} disabled={hasDoc}
            className={`w-full flex items-center gap-4 p-4 rounded-xl border text-left transition-all ${
              hasDoc ? 'border-[rgba(99,203,182,0.25)] bg-[rgba(99,203,182,0.04)]' : 'border-white/10 bg-white/[0.02] hover:border-[rgba(99,203,182,0.15)] hover:bg-white/[0.03]'
            }`} data-testid="client-upload-docs-button">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{background:'var(--rn-green-dim)'}}>
              {hasDoc ? <CheckCircle size={20} style={{color:'var(--rn-green)'}}/> : <Upload size={20} style={{color:'var(--rn-green)'}}/>}
            </div>
            <div className="flex-1">
              <p className={`text-sm font-medium ${hasDoc ? 'text-white/60' : 'text-white/90'}`}>Enviar documento</p>
              <p className="text-[11px] text-white/40">{hasDoc ? 'Documento enviado com sucesso' : 'Envie uma foto do seu documento com CPF'}</p>
            </div>
            {hasDoc ? <span className="badge-success text-[10px]">Concluido</span> : <ArrowRight size={16} className="text-white/20"/>}
          </button>

          {/* Term */}
          <button onClick={() => navigate(`/client/term/${c.id}`)} disabled={hasTerm}
            className={`w-full flex items-center gap-4 p-4 rounded-xl border text-left transition-all ${
              hasTerm ? 'border-[rgba(99,203,182,0.25)] bg-[rgba(99,203,182,0.04)]' : 'border-white/10 bg-white/[0.02] hover:border-[rgba(99,203,182,0.15)] hover:bg-white/[0.03]'
            }`} data-testid="client-sign-term-button">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{background:'var(--rn-green-dim)'}}>
              {hasTerm ? <CheckCircle size={20} style={{color:'var(--rn-green)'}}/> : <PenTool size={20} style={{color:'var(--rn-green)'}}/>}
            </div>
            <div className="flex-1">
              <p className={`text-sm font-medium ${hasTerm ? 'text-white/60' : 'text-white/90'}`}>Assinar Termo de Filiacao</p>
              <p className="text-[11px] text-white/40">{hasTerm ? 'Termo assinado com sucesso' : 'Leia e assine o termo de adesao'}</p>
            </div>
            {hasTerm ? <span className="badge-success text-[10px]">Concluido</span> : <ArrowRight size={16} className="text-white/20"/>}
          </button>
        </div>

        {/* Congratulations message */}
        {clientDone && (
          <div className="mt-5 p-5 rounded-xl text-center" style={{background:'rgba(99,203,182,0.06)', border:'1px solid rgba(99,203,182,0.15)'}}>
            <CheckCircle size={36} className="mx-auto mb-2" style={{color:'var(--rn-green)'}} />
            <p className="font-display text-base font-semibold" style={{color:'var(--rn-green)'}}>Parabens! Voce concluiu sua etapa com sucesso.</p>
            <p className="text-sm text-white/50 mt-1">Agora deixa com a gente. Ja vamos dar inicio ao processo de regularizacao das suas negativacoes.</p>
          </div>
        )}
      </div>

      {/* ========== BLOCO 2: ACOES DA RECUPNET ========== */}
      <div className="glass-card">
        <h2 className="font-display text-lg font-semibold mb-1">Acompanhamento RecupNet</h2>
        <p className="text-xs text-white/40 mb-5">Acompanhe o que estamos fazendo por voce.</p>

        <div className="space-y-0">
          {RECUPNET_STEPS.map((step, i) => {
            const status = getRecupStep(step.key);
            const isNeg = step.key === 'NEG_CLEARING';
            const isLast = i === RECUPNET_STEPS.length - 1;
            return (
              <div key={step.key}>
                <div className="flex items-start gap-3 relative">
                  {/* Connector */}
                  {!isLast && (
                    <div className="absolute left-[11px] top-7 bottom-0 w-0.5" style={{
                      background: status === 'done' ? 'rgba(99,203,182,0.3)' : 'rgba(255,255,255,0.06)'
                    }}/>
                  )}
                  {/* Dot */}
                  <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    status === 'done' ? 'scale-in' : status === 'active' ? 'pulse-glow' : ''
                  }`} style={{
                    background: status === 'done' ? 'var(--rn-green)' : status === 'active' ? 'rgba(99,203,182,0.2)' : 'rgba(255,255,255,0.06)',
                    color: status === 'done' ? '#0B0F10' : status === 'active' ? 'var(--rn-green)' : 'rgba(255,255,255,0.2)',
                  }}>
                    {status === 'done' ? <CheckCircle size={13}/> : <Clock size={11}/>}
                  </div>
                  {/* Content */}
                  <div className={`flex-1 pb-4 ${isLast ? 'pb-0' : ''}`}>
                    <p className={`text-sm font-medium ${status === 'done' ? 'text-white/80' : status === 'active' ? 'text-white/85' : 'text-white/30'}`}>
                      {step.label}
                    </p>
                    {(status === 'active' || status === 'done') && (
                      <p className="text-[11px] text-white/35 mt-0.5">{step.simple}</p>
                    )}
                    {status === 'done' && <span className="badge-success text-[9px] mt-1 inline-block">Concluido</span>}
                    {status === 'active' && <span className="badge-warning text-[9px] mt-1 inline-block">Em andamento</span>}
                  </div>
                </div>

                {/* Neg bureaus sub-items */}
                {isNeg && (status === 'active' || status === 'done') && (
                  <div className="ml-9 mb-3 space-y-1.5">
                    {NEG_BUREAUS.map(bureau => {
                      const done = mt.has(bureau.key);
                      return (
                        <div key={bureau.key} className="flex items-center gap-2 p-2 rounded-lg" style={{
                          background: done ? 'rgba(99,203,182,0.05)' : 'rgba(255,255,255,0.02)',
                          border: `1px solid ${done ? 'rgba(99,203,182,0.15)' : 'rgba(255,255,255,0.05)'}`,
                        }}>
                          {done ? <CheckCircle size={12} style={{color:'var(--rn-green)'}}/> : <Clock size={12} className="text-white/20"/>}
                          <span className={`text-xs ${done ? 'text-white/70' : 'text-white/35'}`}>{bureau.label}</span>
                          <span className={`ml-auto text-[9px] ${done ? 'badge-success' : 'badge-neutral'}`}>
                            {done ? 'Removido' : 'Aguardando'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Activity History */}
      <div className="glass-card">
        <h3 className="font-display text-base font-semibold mb-3">Historico</h3>
        <div className="space-y-2">
          {c.milestones?.length === 0 && <p className="text-xs text-white/30">Nenhuma atividade registrada.</p>}
          {c.milestones?.slice().reverse().map((ms, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <div className="w-1.5 h-1.5 rounded-full" style={{background:'var(--rn-green)'}}/>
              <span className="text-white/60">{getMilestoneLabel(ms.type)}</span>
              <span className="text-white/25 ml-auto">{new Date(ms.occurredAt).toLocaleDateString('pt-BR',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function getMilestoneLabel(type) {
  const labels = {
    'REGISTERED': 'Cadastro realizado',
    'DOCUMENT_UPLOADED': 'Documento enviado',
    'DOCUMENT_APPROVED': 'Documento validado',
    'TERM_SIGNED': 'Termo de Filiacao assinado',
    'PROTOCOL_SENT': 'Documentacao enviada para processamento',
    'ACTION_FILED': 'Processo protocolado',
    'NEG_SPC_CLEARED': 'Negativacao SPC Brasil removida',
    'NEG_BOA_VISTA_CLEARED': 'Negativacao Boa Vista removida',
    'NEG_CENPROT_CLEARED': 'Negativacao CENPROT removida',
    'NEG_SERASA_CLEARED': 'Negativacao Serasa removida',
  };
  return labels[type] || type;
}
